import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { RepaymentCalculator } from "@/components/calculators/RepaymentCalculator";
import { SpotlightRate } from "@/components/admin/SpotlightRate";
import { FAQPageJsonLd } from "@/components/seo/JsonLd";
import { motion } from "framer-motion";
import {
  CircleCheck, ArrowRight,
  Car, RefreshCw, Briefcase, HelpCircle, ShieldCheck, Lock, Landmark,
  Zap, BatteryCharging, PlugZap,
} from "lucide-react";
import gorillaImg from "@/assets/gorilla-workout-suit.webp";
import gorillaCtaImg from "@/assets/gorilla-money-tree.webp";
import carLoansHero from "@/assets/optimized/car-loans-hero.webp";
import { SEOHead } from "@/components/seo/SEOHead";

export type LP5Config = {
  make: string;
  heroHeadline: string;
  heroSub: string;
  calcDefault: number;
  calcMin: number;
  calcMax: number;
  hasDealers: boolean;
  captiveFinance?: string;
};

const CTA_HREF = "/car-loans/comparison-engine";

export default function CarFinanceLP5({ config }: { config: LP5Config }) {
  const { pathname: __seoPath } = useLocation();
  const { make, heroHeadline, heroSub, calcDefault, calcMin, calcMax, hasDealers, captiveFinance } = config;
  const pageSlug = `lp5-${make.toLowerCase().replace(/\s+/g, "-")}`;
  const [amount, setAmount] = useState(calcDefault);
  const [term, setTerm] = useState("5");
  const [purpose, setPurpose] = useState("new");

  const ctaTo = `${CTA_HREF}?amount=${amount}&term=${term}&purpose=${purpose}`;

  const steps = [
    { n: "01", title: "Tell us about your loan", body: "Share your loan amount, term, and what you're buying. Takes about 60 seconds." },
    { n: "02", title: "We compare 40+ lenders", body: "Soft credit check only — no impact on your score. We match you to lenders that suit your profile." },
    {
      n: "03",
      title: "Get your pre-approval",
      body: hasDealers
        ? `Know exactly what you can borrow before you step into the ${make} showroom.`
        : `Know exactly what you can borrow before you start configuring your ${make} online.`,
    },
  ];

  const loanTypes = [
    { icon: Car, title: `New ${make} Finance`, body: hasDealers ? `Finance for new ${make} vehicles from authorised dealers` : `Finance for new ${make} orders placed online`, purpose: "new" },
    { icon: Car, title: `Used ${make} Finance`, body: `Used ${make} vehicles — dealer or private sale`, purpose: "used" },
    { icon: RefreshCw, title: `${make} Refinance`, body: `Already have a ${make} loan? Compare and potentially save`, purpose: "refinance" },
    { icon: Briefcase, title: `Self-Employed ${make} Finance`, body: "ABN holders and self-employed borrowers welcome", purpose: "new" },
  ];

  const faqs = hasDealers
    ? [
        { q: `Can I use LoanGorilla to finance a ${make}?`, a: `Yes. LoanGorilla compares independent finance that works at any authorised ${make} dealership in Australia. You present your pre-approval and the dealership processes the purchase as normal.` },
        { q: `Is ${make} dealer finance different from LoanGorilla?`, a: "Dealer finance is offered by the dealership, usually through a single lender. LoanGorilla compares 40+ independent lenders to find a rate that suits your profile — giving you a broader view of the market before you sign anything." },
        { q: "Will a soft check appear on my credit file?", a: "No. Our eligibility check uses a soft inquiry only — not recorded on your credit file and no impact on your credit score." },
        { q: `I'm buying a used ${make} — does this still apply?`, a: `Yes. LoanGorilla compares finance for new and used ${make} vehicles. The age and condition of the vehicle may affect which lenders are available to you.` },
      ]
    : [
        { q: `Can I finance a ${make} without using ${make}'s own financing?`, a: `Yes. ${make} offers its own financing through ${make.toLowerCase()}.com/au, but you are not required to use it. LoanGorilla compares 40+ lenders — you apply through us, receive your pre-approval, then place your ${make} order as normal.` },
        { q: `How does ${make} finance work without a dealer?`, a: `Once your loan is approved, your lender releases funds directly to ${make} Australia. The process works the same way as a private sale — no dealership required.` },
        { q: "Will a soft check appear on my credit file?", a: "No. Our eligibility check uses a soft inquiry only — not recorded on your credit file and no impact on your credit score." },
        { q: `I'm buying a used ${make} — does this still apply?`, a: `Yes. LoanGorilla compares finance for new and used ${make} vehicles. The age and condition of the vehicle may affect which lenders are available to you.` },
      ];

  const evFeatures = [
    { icon: BatteryCharging, title: "EV-familiar lenders", body: "We work with lenders who understand EV residual values and take an informed approach to electric vehicle finance." },
    { icon: Zap, title: "New and used EV finance", body: `Finance for new ${make} orders and the growing Australian used EV market.` },
    { icon: PlugZap, title: "Finance the full amount", body: "Including home charging installation if bundled in your purchase — subject to lender approval." },
  ];

  const sliderPct = useMemo(() => {
    const pct = ((amount - calcMin) / (calcMax - calcMin)) * 100;
    return Math.min(100, Math.max(0, pct));
  }, [amount, calcMin, calcMax]);

  return (
    <Layout>
  <SEOHead title="Car Finance LP5 | LoanGorilla" description="Car Finance LP5 — compare options and rates with LoanGorilla." canonical={__seoPath} />
      <FAQPageJsonLd faqs={faqs.map((f) => ({ question: f.q, answer: f.a }))} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-primary py-10 md:py-14">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute -bottom-48 -left-24 h-80 w-80 rounded-full bg-primary-foreground/5 blur-3xl" />

        <div className="container relative z-10">
          <div className="grid items-start gap-8 md:grid-cols-[1.1fr_1fr]">
            {/* Left copy */}
            <div className="text-center md:text-left">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent">
                {make} finance — LoanGorilla
              </p>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-3 font-display text-4xl font-bold leading-tight tracking-tight text-primary-foreground md:text-5xl lg:text-6xl"
              >
                {heroHeadline}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mx-auto mt-5 max-w-xl text-lg text-primary-foreground/90 md:mx-0"
              >
                {heroSub}
              </motion.p>

              {/* Compliance rate block (light) */}
              <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-2 rounded-xl bg-primary-foreground/10 px-5 py-3 text-sm text-primary-foreground/90 md:justify-start">
                <span>
                  <span className="font-bold text-primary-foreground">
                    <SpotlightRate pageSlug={pageSlug} slotId="hero-advertised" fallback="5.39% p.a." suffix="% p.a." />
                  </span>{" "}
                  advertised rate
                </span>
                <span className="hidden h-4 w-px bg-primary-foreground/20 sm:block" />
                <span>
                  <span className="font-bold text-primary-foreground">
                    <SpotlightRate pageSlug={pageSlug} slotId="hero-comparison" fallback="5.63% p.a." suffix="% p.a." />
                  </span>{" "}
                  comparison rate*
                </span>
              </div>
              <p className="mt-2 text-[11px] text-primary-foreground/50 md:text-left">
                *Comparison rate based on a $30,000 loan over 5 years. Fees and charges apply.
              </p>

              <div className="mt-6 flex justify-center md:justify-start">
                <Button
                  asChild
                  size="lg"
                  className="h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base px-8"
                >
                  <Link to={ctaTo}>Check My Eligibility</Link>
                </Button>
              </div>

              {/* Trust signals */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-primary-foreground/85 md:justify-start">
                <div className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-accent" /><span>No credit score impact</span></div>
                <div className="flex items-center gap-1.5"><Lock className="h-4 w-4 text-accent" /><span>256-bit encryption</span></div>
                <div className="flex items-center gap-1.5"><Landmark className="h-4 w-4 text-accent" /><span>40+ lenders</span></div>
              </div>
            </div>

            {/* Right application card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="rounded-2xl bg-background p-6 shadow-2xl shadow-black/20 md:p-7"
            >
              <h2 className="font-display text-xl font-bold text-foreground">
                Check your {make} finance eligibility
              </h2>

              {/* Loan amount */}
              <div className="mt-5">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-foreground">Loan amount</label>
                  <span className="font-display text-lg font-bold text-primary">${amount.toLocaleString()}</span>
                </div>
                <input
                  type="range"
                  min={calcMin}
                  max={calcMax}
                  step={1000}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="mt-2 w-full accent-primary"
                  style={{
                    background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${sliderPct}%, hsl(var(--muted)) ${sliderPct}%, hsl(var(--muted)) 100%)`,
                  }}
                />
                <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                  <span>${calcMin.toLocaleString()}</span>
                  <span>${calcMax.toLocaleString()}</span>
                </div>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value) || 0)}
                  className="sr-only"
                />
              </div>

              {/* Loan term */}
              <div className="mt-5">
                <label className="text-sm font-semibold text-foreground">Loan term</label>
                <div className="mt-2 grid grid-cols-7 gap-1.5">
                  {[1, 2, 3, 4, 5, 6, 7].map((y) => (
                    <button
                      key={y}
                      type="button"
                      onClick={() => setTerm(String(y))}
                      className={`rounded-md border py-2 text-sm font-semibold transition-colors ${
                        term === String(y)
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background text-foreground hover:border-primary/50"
                      }`}
                    >
                      {y}y
                    </button>
                  ))}
                </div>
              </div>

              {/* Loan purpose */}
              <div className="mt-5">
                <label className="text-sm font-semibold text-foreground">Loan purpose</label>
                <div className="mt-2 grid grid-cols-3 gap-1.5">
                  {[
                    { v: "new", l: `New ${make}` },
                    { v: "used", l: `Used ${make}` },
                    { v: "refinance", l: "Refinance" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setPurpose(o.v)}
                      className={`rounded-md border py-2 text-xs font-semibold transition-colors ${
                        purpose === o.v
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background text-foreground hover:border-primary/50"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                asChild
                size="lg"
                className="mt-6 w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base"
              >
                <Link to={ctaTo}>Check My Eligibility <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                <CircleCheck className="h-3.5 w-3.5 text-primary" />
                Soft check only — no impact on your credit score
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b bg-muted/40 py-6">
        <div className="container grid grid-cols-2 gap-6 text-center">
          <div>
            <p className="font-display text-3xl font-bold text-primary md:text-4xl">40+</p>
            <p className="text-sm text-muted-foreground">Car loan lenders</p>
          </div>
          <div>
            <p className="font-display text-3xl font-bold text-primary md:text-4xl">Soft check</p>
            <p className="text-sm text-muted-foreground">No credit score impact</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-14 md:py-20">
        <div className="container">
          <h2 className="text-center font-display text-3xl font-bold md:text-4xl">
            Three steps to knowing your budget
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {steps.map((s) => (
              <Card key={s.n} className="border-2 hover:border-primary/30 transition-colors">
                <CardContent className="space-y-3 p-6">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 font-display text-lg font-bold text-primary">
                    {s.n}
                  </div>
                  <h3 className="font-display text-lg font-bold">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to={ctaTo} className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
              Start your eligibility check <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* EV TRUST SECTION */}
      <section className="border-y bg-background py-14 md:py-20">
        <div className="container max-w-5xl">
          <h2 className="text-center font-display text-3xl font-bold md:text-4xl">
            Finance for Australia's growing EV market
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {evFeatures.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border-2 border-border bg-card p-6 transition-colors hover:border-primary/40">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent/15 text-accent-foreground">
                  <Icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRE-APPROVAL ADVANTAGE */}
      <section className="bg-muted/40 py-14 md:py-20">
        <div className="container grid items-center gap-10 md:grid-cols-2">
          <div className="overflow-hidden rounded-2xl order-2 md:order-1">
            <img src={gorillaImg} alt="LoanGorilla mascot" className="h-full w-full object-cover" />
          </div>
          <div className="order-1 md:order-2">
            <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-primary">Pre-approval advantage</p>
            <h2 className="font-display text-3xl font-bold md:text-4xl">
              {hasDealers
                ? `Walk into the ${make} showroom knowing your budget`
                : `Know your ${make} budget before you configure`}
            </h2>
            <p className="mt-4 text-muted-foreground">
              {hasDealers
                ? `Pre-approval means you negotiate on the car price — not the finance terms. When you know your borrowing power before visiting the ${make} dealer, you're in a stronger position at every step.`
                : `${make} dealerships are a great way to see what you're buying. But knowing your borrowing power before you start configuring your ${make} online means you can make confident choices about specs, range, and options without budget surprises at checkout.`}
            </p>
            <ul className="mt-5 space-y-2">
              {(hasDealers
                ? [
                    "Know your maximum borrowing power",
                    "Negotiate on price — not finance pressure",
                    "No surprises at the dealership",
                    "Walk away if the deal doesn't suit you",
                  ]
                : [
                    "Know your maximum borrowing power",
                    "Choose specs and options with confidence",
                    "No surprises when you place your order",
                    "Walk away if the deal doesn't suit you",
                  ]
              ).map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm">
                  <CircleCheck className="h-4 w-4 text-primary" /> {item}
                </li>
              ))}
            </ul>
            <Link to={ctaTo} className="mt-6 inline-flex items-center gap-1 font-semibold text-primary hover:underline">
              {hasDealers ? "Get pre-approved before your dealer visit" : "Get pre-approved before you order"} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="py-14 md:py-20">
        <div className="container max-w-4xl space-y-6">
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">Estimate your repayments</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              Plug in your numbers to see what a {make} loan could look like before you apply.
            </p>
          </div>
          <RepaymentCalculator />
          <div className="rounded-2xl border bg-muted/30 p-6 md:p-8">
            <h3 className="mb-3 font-display text-lg font-bold">Calculator assumptions</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              This tool is a general guide only. It uses the repayment, rate and term you enter to estimate loan amounts and repayments, but it doesn't include all lender fees, government charges or product features. Actual offers will depend on your credit history, income, expenses, the car you choose and each lender's criteria. The results are not a quote, pre-approval or personal advice — use them as a starting point, then compare real products before you decide.
            </p>
          </div>
          <div className="text-center">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
              <Link to={ctaTo}>Get your personalised rate — Check My Eligibility <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FINANCE OPTIONS */}
      <section className="bg-muted/40 py-14 md:py-20">
        <div className="container max-w-5xl">
          <h2 className="text-center font-display text-3xl font-bold md:text-4xl">
            {make} finance for every buyer
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6">
            {loanTypes.map(({ icon: Icon, title, body, purpose: p }) => (
              <Link
                key={title}
                to={`${CTA_HREF}?amount=${amount}&term=${term}&purpose=${p}`}
                className="group relative flex items-center gap-4 rounded-2xl border-2 border-border bg-background p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-lg md:p-6"
              >
                <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-base font-bold md:text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground">{body}</p>
                </div>
                <ArrowRight className="h-5 w-5 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
              <Link to={ctaTo}>Check My Eligibility <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CREDIT SCORE OBJECTION */}
      <section className="bg-primary py-14 md:py-20 text-primary-foreground">
        <div className="container max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold md:text-4xl">Worried about your credit score?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-primary-foreground/80">
            Our eligibility check uses a soft credit inquiry only — the kind that never appears on your credit file and has zero impact on your credit score. Check as many times as you like.
          </p>
          <ul className="mx-auto mt-6 max-w-xl space-y-2 text-left">
            {[
              "Soft inquiry — not recorded on your credit file",
              "No impact on your credit score",
              "Hard enquiry only if you choose to proceed with a lender",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CircleCheck className="mt-0.5 h-5 w-5 text-accent" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Button asChild size="lg" className="mt-8 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
            <Link to={ctaTo}>Check My Eligibility <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/50 py-14 md:py-20">
        <div className="container max-w-4xl">
          <h2 className="mb-8 text-center font-display text-2xl font-bold md:text-3xl">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible>
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left font-display font-semibold">
                  <span className="flex items-center gap-2">
                    <HelpCircle className="h-4 w-4 text-primary" />
                    {faq.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-muted/40 py-12 md:py-20">
        <div className="container">
          <div className="relative mx-auto max-w-6xl overflow-hidden rounded-3xl bg-primary shadow-2xl">
            <div className="pointer-events-none absolute -right-48 -top-48 h-96 w-96 rounded-full bg-accent/20 blur-[100px]" />
            <div className="pointer-events-none absolute -bottom-48 -left-48 h-96 w-96 rounded-full bg-accent/10 blur-[100px]" />

            <div className="relative flex flex-col items-center gap-10 px-8 py-14 md:flex-row md:justify-between md:gap-12 md:px-16 md:py-16">
              <div className="z-10 flex-1 text-center md:text-left">
                <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-primary-foreground md:text-5xl">
                  Finance your {make}{" "}
                  <span className="bg-gradient-to-r from-accent to-accent/70 bg-clip-text text-transparent">
                    with confidence
                  </span>
                </h2>

                <p className="mx-auto mt-5 max-w-lg text-lg text-primary-foreground/90 md:mx-0">
                  Get pre-approved in minutes. No impact on your credit score. Join thousands of happy Aussie drivers.
                </p>

                <div className="mt-8 flex flex-col items-center gap-6 sm:flex-row md:items-center">
                  <Button
                    asChild
                    size="lg"
                    className="group h-14 bg-accent px-8 text-base font-bold text-accent-foreground shadow-[0_0_30px_hsl(var(--accent)/0.35)] transition-all hover:scale-105 hover:bg-accent/90 active:scale-95"
                  >
                    <Link to={ctaTo}>
                      Check My Eligibility
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>

                <div className="mt-10 grid grid-cols-2 gap-3 text-sm text-primary-foreground/85 lg:grid-cols-4">
                  {["Soft credit check", "40+ lenders", "Under 2 minutes", "100% free"].map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <CircleCheck className="h-4 w-4 shrink-0 text-accent" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative flex flex-1 justify-center md:justify-end">
                <div className="relative flex w-full max-w-[360px] items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-accent/20 blur-3xl" />
                  <img
                    src={gorillaCtaImg}
                    alt="LoanGorilla mascot"
                    className="relative z-10 w-full rounded-2xl object-cover"
                  />
                  <div className="absolute -left-4 -top-4 z-20 -rotate-6 rounded-2xl border border-primary-foreground/20 bg-primary-foreground/10 p-4 shadow-xl backdrop-blur-md">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-primary-foreground">Financing:</p>
                    <p className="font-display text-xl font-black text-primary-foreground">{make} drivers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hidden hero asset preload reference */}
      <link rel="preload" as="image" href={carLoansHero} />
    </Layout>
  );
}

const standardEV = (make: string, calcDefault: number, calcMin: number, calcMax: number): LP5Config => ({
  make,
  heroHeadline: `${make} finance — made easy`,
  heroSub: `Explore finance options for your ${make} with expert help, a range of lenders, and avoiding the noise of the dealership.`,
  calcDefault, calcMin, calcMax,
  hasDealers: true,
});

export const LP5_CONFIGS: Record<string, LP5Config> = {
  tesla: {
    make: "Tesla",
    heroHeadline: "Tesla finance — made easy",
    heroSub: "Explore finance options for your Tesla with expert help, a range of lenders, and avoiding the noise of the dealership,",
    calcDefault: 75000, calcMin: 20000, calcMax: 200000,
    hasDealers: false,
    captiveFinance: "Tesla's own financing (available through tesla.com/au)",
  },
  byd: standardEV("BYD", 50000, 20000, 120000),
  polestar: standardEV("Polestar", 75000, 20000, 200000),
  geely: standardEV("Geely", 45000, 20000, 120000),
  zeekr: standardEV("Zeekr", 45000, 20000, 120000),
  jaecoo: standardEV("Jaecoo", 45000, 20000, 120000),
  chery: standardEV("Chery", 45000, 20000, 120000),
};

